import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Grid from '@material-ui/core/Grid';
import { IconButton } from '@material-ui/core';
import copy from 'copy-to-clipboard';

import { CopyButton } from 'core/components/copy-button';
import { Tooltip } from 'core/components/tooltip';
import { Label } from '../label';
import icLocked from 'core/assets/svg/icLocked.svg';
import useStyles from './value-field.styles';

const blankValues = ['', null, undefined, 'Unknown', 'unknown', 'N/A', 'n/a'];

function ValueField({
  label,
  value,
  style,
  mode,
  formatter,
  className,
  showTooltip,
  lockedValue,
  copyable,
  action,
  link,
  ...props
}) {
  const classes = useStyles();

  const renderValue = () => {
    const formattedValue = formatter ? formatter(value) : value;

    if (link) {
      return (
        <a href={link} target="_blank" rel="noopener noreferrer" className={classes.link}>
          {formattedValue}
        </a>
      );
    }

    return formattedValue;
  };

  return (
    <div className={classNames(classes.container, className)} data-cy="value-field" style={style} {...props}>
      <Label>
        {label && (
          <Label type="infoLabel" className={classes[mode ? `${mode}-label` : 'label']}>
            {label}
          </Label>
        )}
        {showTooltip && (
          <Tooltip
            className={classes.valueFieldTooltip}
            text={showTooltip.onHoverText}
            label={showTooltip.label || 'More Info'}
            clickIcon={showTooltip.clickIcon}
            onClick={showTooltip.onClick}
          />
        )}
      </Label>
      <Label
        id="value"
        type={blankValues.includes(value) ? 'italicLight' : 'infoValue'}
        className={classes[mode ? `${mode}-value` : 'value']}
      >
        <Grid container direction="row" alignItems="center">
          {renderValue()}
          {copyable && (
            <CopyButton
              onCopy={() => copy(value)}
              className={classes.copyButton}
              aria-label={`Copy ${label || value}`}
            />
          )}
          {action && (
            <IconButton className={classes.copyButton} onClick={action.onClick} disabled={action.disabled}>
              <img src={action.icon} alt={action.alt || 'Action'} width={16} height={16} />
            </IconButton>
          )}
          {lockedValue && (
            <Tooltip text={lockedValue?.text} placement="top" smallIcon={{ src: icLocked }} iconSize="icon16" />
          )}
        </Grid>
      </Label>
    </div>
  );
}

ValueField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  style: PropTypes.object,
  mode: PropTypes.oneOf([null, 'light', 'dark', 'big-font-dark']),
  formatter: PropTypes.func,
  className: PropTypes.string,
  showTooltip: PropTypes.shape({
    label: PropTypes.string,
    onHoverText: PropTypes.string,
    clickIcon: PropTypes.string,
    onClick: PropTypes.func
  }),
  copyable: PropTypes.bool,
  action: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    alt: PropTypes.string,
    disabled: PropTypes.bool
  }),
  lockedValue: PropTypes.object,
  link: PropTypes.string
};

ValueField.defaultProps = {
  label: '',
  value: null,
  style: {},
  mode: null,
  formatter: null,
  className: '',
  showTooltip: null,
  copyable: false,
  action: null,
  lockedValue: null,
  link: null
};

export default ValueField;
